import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { AuxEntity, getListadoGranos } from "@/services/aux-entities.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { ProductionGrainForm } from "../components/ProductionGrainForm";
import { ProductionGrainListEntity } from "../schema/production-grains.schema";
import { hasEmptyProductionGrainFormValues } from "../utils/production-grain-form";
import {
  ProductionGrainCreationForm,
  productionGrainCreationSchema,
} from "../create/schema/production-grain-creation.schema";
import {
  fetchProductionGrainHandler,
  updateProductionGrainHandler,
} from "./schema/handler-functions";

export const ProductionGrainDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loadingProductionGrain, setLoadingProductionGrain] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [grains, setGrains] = useState<AuxEntity[] | null>([]);
  const [loadingGrains, setLoadingGrains] = useState(false);
  const [productionGrain, setProductionGrain] =
    useState<ProductionGrainListEntity | null>(null);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchGrains = async () => {
    setLoadingGrains(true);
    const response = await getListadoGranos();
    setGrains(response);
    setLoadingGrains(false);
  };

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ProductionGrainCreationForm>({
    resolver: zodResolver(productionGrainCreationSchema),
    defaultValues: {
      nombre: "",
      aplicaPPMB: undefined,
      granoAdmis: "",
      sumaHA: undefined,
    },
  });

  useEffect(() => {
    if (isActive && id) {
      fetchGrains();
      fetchProductionGrainHandler({
        id: Number(id),
        setLoadingProductionGrain,
        setProductionGrain,
        navigate,
        fetchPermissions,
      });
    }
  }, [isActive, id]);

  useEffect(() => {
    if (!productionGrain) return;

    setValue("nombre", productionGrain.nombre);
    setValue("granoAdmis", String(productionGrain.granoAdmis));
    setValue("aplicaPPMB", [productionGrain.aplicaPPMB]);
    setValue("sumaHA", [productionGrain.sumaHA]);
  }, [productionGrain, setValue]);

  const watchedValues = useWatch({ control });

  const hasNoFilledValues = useMemo(() => {
    return hasEmptyProductionGrainFormValues(watchedValues);
  }, [watchedValues]);

  const handleCancel = () => {
    navigate(AppRoutes.PRODUCTION_GRAINS);
  };

  const onSubmit = async (values: ProductionGrainCreationForm) => {
    if (!id) return;

    await updateProductionGrainHandler({
      id: Number(id),
      values,
      setLoadingUpdate,
      navigate,
      fetchPermissions,
    });
  };

  const loading = loadingProductionGrain || loadingUpdate;

  return (
    <Flex
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      flexDir="column"
      paddingTop="20px"
      gap="12px"
    >
      <SectionHeader
        title="Editar grano productivo"
        addButtonLabel="Guardar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        addButtonType="submit"
        disableAdd={hasNoFilledValues || loading}
        loading={loading}
      />

      <ProductionGrainForm
        control={control}
        errors={errors}
        grains={grains}
        isActive={isActive}
        loading={loading}
        loadingGrains={loadingGrains}
        comboboxKey={
          grains?.length
            ? "production-grain-detail-loaded"
            : "production-grain-detail-empty"
        }
      />
    </Flex>
  );
};
